import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch,
  Divider,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import {
  LightMode as LightIcon,
  DarkMode as DarkIcon,
  SettingsBrightness as AutoIcon,
  Language as LanguageIcon,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useSnackbar } from 'notistack';

const languages = [
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
];

function SettingsPage() {
  const { t, i18n } = useTranslation();
  const { mode, setMode } = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const handleThemeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMode(event.target.value as 'light' | 'dark' | 'system');
    enqueueSnackbar(t('common.success'), { variant: 'success' });
  };

  const handleLanguageChange = (langCode: string) => {
    i18n.changeLanguage(langCode);
    enqueueSnackbar(t('common.success'), { variant: 'success' });
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>{t('settings.title')}</Typography>

      {/* Language */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LanguageIcon /> {t('settings.language')}
          </Typography>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>{t('settings.language')}</InputLabel>
            <Select
              value={i18n.language}
              label={t('settings.language')}
              onChange={(e) => handleLanguageChange(e.target.value)}
            >
              {languages.map((lang) => (
                <MenuItem key={lang.code} value={lang.code}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{lang.flag}</span> {lang.name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* Appearance */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>{t('settings.appearance')}</Typography>
          <FormControl component="fieldset">
            <FormLabel component="legend">{t('settings.theme')}</FormLabel>
            <RadioGroup value={mode} onChange={handleThemeChange}>
              <FormControlLabel
                value="light"
                control={<Radio />}
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <LightIcon /> {t('settings.themeLight')}
                  </Box>
                }
              />
              <FormControlLabel
                value="dark"
                control={<Radio />}
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <DarkIcon /> {t('settings.themeDark')}
                  </Box>
                }
              />
              <FormControlLabel
                value="system"
                control={<Radio />}
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <AutoIcon /> {t('settings.themeSystem')}
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>
        </CardContent>
      </Card>

      {/* Notifications */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>{t('settings.notifications')}</Typography>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography>Streak-Erinnerungen</Typography>
              <Typography variant="body2" color="text.secondary">
                Tägliche Erinnerung zum Lernen
              </Typography>
            </Box>
            <Switch defaultChecked />
          </Box>
          <Divider sx={{ my: 2 }} />
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography>Badge-Benachrichtigungen</Typography>
              <Typography variant="body2" color="text.secondary">
                Benachrichtigung bei neuen Badges
              </Typography>
            </Box>
            <Switch defaultChecked />
          </Box>
          <Divider sx={{ my: 2 }} />
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography>Kurs-Updates</Typography>
              <Typography variant="body2" color="text.secondary">
                Benachrichtigung über neue Kurse
              </Typography>
            </Box>
            <Switch defaultChecked />
          </Box>
        </CardContent>
      </Card>

      {/* Privacy */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>{t('settings.privacy')}</Typography>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography>{t('settings.showOnLeaderboard')}</Typography>
              <Typography variant="body2" color="text.secondary">
                Ihren Namen und Punkte in der Rangliste anzeigen
              </Typography>
            </Box>
            <Switch defaultChecked />
          </Box>
        </CardContent>
      </Card>

      {/* Session */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>{t('settings.account')}</Typography>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography>Automatische Abmeldung</Typography>
              <Typography variant="body2" color="text.secondary">
                Nach 30 Minuten Inaktivität
              </Typography>
            </Box>
            <Switch defaultChecked />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default SettingsPage;
